import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCustomToken,
} from "firebase/auth";
import {
  auth,
  linkEmailToAuthUser,
  checkTherapistExistsByEmail,
  checkTherapistExistsByPhone,
  checkPhoneAllowedForRegistration,
  sendCustomOtp,
  verifyCustomOtp,
} from "../../../firebase";
import {
  prevStep,
  selectAboutYou,
  selectClinicInfo,
  selectSpecializations,
  selectPreferences,
  selectLogo,
} from "../../../store/slices/registrationSlice";
import {
  setTherapistProfile,
  setClinicProfile,
  setLoading,
} from "../../../store/slices/authSlice";
import { useCreateTherapistAndClinicMutation } from "../../../store/services/therapistApi";
import { normalizeCalendarStartHour } from "../../../utils/calendarHours";
import { useCreateCallRequestMutation } from "../../../store/services/nivApi";
import {
  ShieldCheck,
  Phone,
  ArrowRight,
  Check,
  Headset,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import LoaderLoop from "../../shared/loader/LoaderLoop";
import CustomClinikitButton from "../../ui/CustomClinikitButton";
import PhoneInput from "../../ui/PhoneInput";
import SegmentedOTPInput from "../../ui/SegmentedOTPInput";
import "./Steps.css";
import { trackPixelEvent } from "../../../hooks/useFacebookPixel";
import { CATEGORY_COLORS } from "../../../utils/treatmentTypeRules";

// General type first, then user rows; empty duration/rate → null (inherit clinic defaults).
const buildTreatmentTypesPayload = (prefs) => {
  const toNumOrNull = (v) =>
    v !== "" && v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
  const general = {
    name: (prefs.generalTreatmentName || "טיפול כללי").trim(),
    color: CATEGORY_COLORS[0],
    duration: Number(prefs.defaultMeetingTime) || 45,
    rate: toNumOrNull(prefs.defaultHourlyRate),
  };
  const rows = (prefs.treatmentTypes || [])
    .filter((r) => r && String(r.name || "").trim())
    .map((r) => ({
      name: String(r.name).trim(),
      color: r.color || CATEGORY_COLORS[1],
      duration: toNumOrNull(r.duration),
      rate: toNumOrNull(r.rate),
    }));
  return [general, ...rows];
};

function VerificationStep() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Selectors
  const aboutYou = useSelector(selectAboutYou);
  const clinicInfo = useSelector(selectClinicInfo);
  const specializations = useSelector(selectSpecializations);
  const preferences = useSelector(selectPreferences);
  const logo = useSelector(selectLogo);

  // RTK Query mutations
  const [createTherapistAndClinic, { isLoading: isRegistering }] =
    useCreateTherapistAndClinicMutation();
  const [createCallRequest] = useCreateCallRequestMutation();

  // State
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmationResult, setConfirmationResult] = useState(null);
  // Custom-OTP fallback: engaged only when Firebase Phone Auth SMS is blocked.
  const [usingCustomOtp, setUsingCustomOtp] = useState(false);
  const [customOtpPhone, setCustomOtpPhone] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [wantsOnboarding, setWantsOnboarding] = useState(false);

  // Refs
  const otpInputRef = useRef(null);
  const recaptchaRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // RecaptchaVerifier throws "reCAPTCHA placeholder element must be empty" if
  // the container holds leftover widget DOM (stale prerendered HTML, or a
  // verifier nulled without clear()).
  const emptyRecaptchaContainer = () => {
    const container = document.getElementById("recaptcha-container");
    if (container) {
      container.innerHTML = "";
    }
  };

  // Build a fresh invisible reCAPTCHA verifier. Invisible reCAPTCHA tokens are
  // single-use — a verifier that already produced a signInWithPhoneNumber (or one
  // created on mount that later expired) fails on reuse, which surfaces as the
  // "try again" loop and eventually auth/too-many-requests. So we tear down and
  // recreate right before every send instead of reusing a long-lived instance.
  const freshRecaptcha = () => {
    if (recaptchaRef.current) {
      try {
        recaptchaRef.current.clear();
      } catch (e) {}
      recaptchaRef.current = null;
    }
    emptyRecaptchaContainer();
    recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {
        try {
          recaptchaRef.current?.clear();
        } catch (e) {}
        recaptchaRef.current = null;
      },
    });
    return recaptchaRef.current;
  };

  // Clear the verifier when leaving the step so its widget DOM doesn't leak.
  useEffect(() => {
    return () => {
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch (e) {}
        recaptchaRef.current = null;
      }
    };
  }, []);

  const handleSendCode = async () => {
    if (isSubmittingRef.current) return;

    if (!phoneNumber || phoneNumber.length < 10) {
      setError("יש להזין מספר טלפון תקין");
      return;
    }

    isSubmittingRef.current = true;
    setError("");
    setDuplicateError("");
    setIsSendingCode(true);
    setIsCheckingDuplicate(true);

    const formattedPhone = phoneNumber.startsWith("+")
      ? phoneNumber
      : `+972${phoneNumber.replace(/^0/, "")}`;

    // Pre-send checks (Firestore) live in their own try/catch so a Firestore
    // failure reads as "בדיקת הפרטים" and can't masquerade as a phone/SMS error.
    try {
      // Whitelist gate — registration is invite-only. Phone must be approved by
      // a NiV admin in /niv_registration_whitelist (real enforcement is in
      // firestore.rules; this is just for UX so we don't burn an SMS).
      const allowed = await checkPhoneAllowedForRegistration(formattedPhone);
      if (!allowed) {
        setIsCheckingDuplicate(false);
        setDuplicateError(
          "מספר הטלפון אינו מורשה להרשמה. ההרשמה למערכת היא בהזמנה בלבד — אנא קבעו שיחת הדרכה ונחזור אליכם בהקדם."
        );
        setIsSendingCode(false);
        isSubmittingRef.current = false;
        return;
      }

      // Check if email or phone already exist in the system
      const [emailExists, phoneExists] = await Promise.all([
        aboutYou.email
          ? checkTherapistExistsByEmail(aboutYou.email.trim().toLowerCase())
          : Promise.resolve(false),
        checkTherapistExistsByPhone(formattedPhone),
      ]);

      setIsCheckingDuplicate(false);

      if (emailExists || phoneExists) {
        const field = emailExists && phoneExists
          ? "כתובת האימייל ומספר הטלפון כבר רשומים"
          : emailExists
          ? "כתובת האימייל כבר רשומה"
          : "מספר הטלפון כבר רשום";

        setDuplicateError(
          `${field} במערכת. אם כבר נרשמתם, אנא התחברו דרך מסך הכניסה.`
        );
        setIsSendingCode(false);
        isSubmittingRef.current = false;
        return;
      }
    } catch (err) {
      console.error("[CK-AUTH-006] Pre-send checks failed:", err);
      setError("שגיאה בבדיקת הפרטים. נסו שוב.");
      setIsCheckingDuplicate(false);
      setIsSendingCode(false);
      isSubmittingRef.current = false;
      return;
    }

    // SMS send — isolated from the Firestore checks above.
    try {
      const appVerifier = freshRecaptcha();
      const result = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        appVerifier
      );
      setConfirmationResult(result);
      setIsCodeSent(true);
      setCountdown(60);

      setTimeout(() => otpInputRef.current?.focus(), 100);
    } catch (err) {
      console.error("[CK-AUTH-002] SMS send error:", err);

      // The invisible reCAPTCHA verifier is single-use; drop it before any retry.
      if (recaptchaRef.current) {
        try {
          recaptchaRef.current.clear();
        } catch (e) {}
        recaptchaRef.current = null;
      }

      // Firebase blocked the SMS (abuse detection: too-many-requests or the
      // internal error-code:-39). Fall back to our own OTP over ActiveTrail —
      // silent to the user, same code-entry UI.
      const firebaseBlocked =
        err.code === "auth/too-many-requests" ||
        err.code?.includes("error-code:-39") ||
        err.message?.includes("error-code:-39");

      if (firebaseBlocked) {
        try {
          const res = await sendCustomOtp(formattedPhone, "register");
          if (res?.success) {
            setUsingCustomOtp(true);
            setCustomOtpPhone(formattedPhone);
            setIsCodeSent(true);
            setCountdown(60);
            setTimeout(() => otpInputRef.current?.focus(), 100);
            return;
          }
          console.error("[CK-AUTH-007] Custom OTP fallback rejected:", {
            errorCode: "CK-AUTH-007",
            fallbackError: res?.error,
            timestamp: new Date().toISOString(),
          });
        } catch (fallbackErr) {
          console.error("[CK-AUTH-007] Custom OTP fallback error:", {
            errorCode: "CK-AUTH-007",
            errorMessage: fallbackErr.message,
            timestamp: new Date().toISOString(),
          });
        }
        // Fallback unavailable too — show the original guidance.
        setError("שירות ה-SMS זמנית אינו זמין. אנא נסו שוב בעוד מספר דקות.");
      } else {
        setError("שגיאה בשליחת הקוד. נסו שוב.");
      }
    } finally {
      setIsSendingCode(false);
      setIsCheckingDuplicate(false);
      isSubmittingRef.current = false;
    }
  };

  const handleCodeChange = (digits) => {
    setVerificationCode(digits);
    if (error) setError("");
  };

  const handleCodeComplete = (digits) => {
    if (digits.length === 6) {
      handleVerifyAndRegister(digits);
    }
  };

  const handleVerifyAndRegister = async (code) => {
    if (isSubmittingRef.current) return;

    if (!usingCustomOtp && !confirmationResult) {
      setError("יש לשלוח קוד אימות קודם");
      return;
    }

    isSubmittingRef.current = true;
    setError("");

    try {
      // Verify the code — custom-OTP fallback or Firebase Phone Auth. Both end
      // with a signed-in Firebase Auth user carrying the phone_number claim, so
      // the subsequent client-side clinic/therapist writes pass firestore.rules.
      let user;
      let registrationMethod;
      if (usingCustomOtp) {
        const res = await verifyCustomOtp(customOtpPhone, code, "register");
        if (!res?.success) {
          if (res?.error === "INVALID_CODE") {
            setError("קוד שגוי. אנא נסו שוב.");
          } else if (res?.error === "CODE_EXPIRED") {
            setError("הקוד פג תוקף. שלחו קוד חדש.");
          } else if (res?.error === "TOO_MANY_ATTEMPTS") {
            setError("יותר מדי ניסיונות. שלחו קוד חדש.");
          } else {
            setError("שגיאה באימות. נסו שוב.");
          }
          setVerificationCode("");
          setTimeout(() => otpInputRef.current?.focus(), 50);
          isSubmittingRef.current = false;
          return;
        }
        const cred = await signInWithCustomToken(auth, res.customToken);
        user = cred.user;
        registrationMethod = "custom_otp";
      } else {
        const userCredential = await confirmationResult.confirm(code);
        user = userCredential.user;
        registrationMethod = "firebase_otp";
      }

      // Prepare user data for registration
      const normalizedEmail = aboutYou.email?.trim().toLowerCase() || '';
      const userData = {
        phone: user.phoneNumber, // Firebase expects 'phone'
        registrationMethod, // "firebase_otp" | "custom_otp"
        // About You
        fullName: aboutYou.fullName,
        email: normalizedEmail,
        gender: aboutYou.gender,
        licenseNumber: aboutYou.licenseNumber || null,
        // Clinic Info
        clinicName: clinicInfo.clinicName,
        clinicType: clinicInfo.clinicType,
        customTypeName: clinicInfo.customTypeName || null,
        businessId: clinicInfo.businessId || null,
        clinicAddress: clinicInfo.clinicAddress || null,
        vatType: clinicInfo.vatType || null,
        // Specializations
        specializations: specializations.specializations || [],
        subSpecializations: specializations.subSpecializations || [],
        ageRange: specializations.ageRange || [],
        // Work preferences (step 4) — general type = clinic defaults
        defaultHourlyRate:
          preferences.defaultHourlyRate !== "" &&
          preferences.defaultHourlyRate != null &&
          !Number.isNaN(Number(preferences.defaultHourlyRate))
            ? Number(preferences.defaultHourlyRate)
            : null,
        defaultMeetingTime: Number(preferences.defaultMeetingTime) || 45,
        clinicManagesCouplesAsPatients: preferences.clinicManagesCouplesAsPatients === true,
        tempCustomersEnabled: preferences.tempCustomersEnabled === true,
        onlineMeetingsEnabled: preferences.onlineMeetingsEnabled === true,
        calendarStartHour: normalizeCalendarStartHour(preferences.calendarStartHour),
        treatmentTypes: buildTreatmentTypesPayload(preferences),
        // Logo file
        logoFile: logo.file || null,
      };

      // Register the therapist and create clinic
      const result = await createTherapistAndClinic({
        uid: user.uid,
        userData,
      }).unwrap();

      // Link email on Firebase Auth account (phone + email on same UID)
      if (normalizedEmail) {
        try {
          await linkEmailToAuthUser(normalizedEmail, null);
        } catch (linkErr) {
          console.error("[CK-AUTH-005] Email link to auth failed (non-blocking):", linkErr.message);
        }
      }

      // Update Redux with the complete profile data (including logo)
      // This ensures the dashboard has the data before rendering
      if (result) {
        dispatch(setTherapistProfile(result.therapist));
        dispatch(setClinicProfile(result.clinic));

        // Meta Pixel: signup completed + 30-day trial started (fires on /signup, no PII).
        trackPixelEvent("CompleteRegistration", { content_name: "signup" });
        trackPixelEvent("StartTrial", { value: "0.00", currency: "ILS", predicted_ltv: "0.00" });
        
        // Preload clinic logo if exists before navigating
        if (result.clinic?.logoUrl) {
          await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = result.clinic.logoUrl;
          });
        }
        
        dispatch(setLoading(false));
      }

      // Create call request if user requested onboarding call
      if (wantsOnboarding) {
        try {
          await createCallRequest({
            requestData: {
              phone: user.phoneNumber,
              fullName: aboutYou.fullName,
              clinicName: clinicInfo.clinicName,
              email: aboutYou.email,
              source: "signup",
            },
          }).unwrap();
        } catch (callError) {
          // Don't block registration if call request fails
          console.error("[CK-AUTH-004] Error creating call request:", callError);
        }
      }

      // Navigate to dashboard
      navigate("/dashboard");
    } catch (err) {
      console.error("[CK-AUTH-003] Verification error:", err);
      const msg = String(err?.message || "");
      if (err.code === "auth/invalid-verification-code") {
        setError("קוד שגוי. אנא נסו שוב.");
      } else if (err.code === "auth/code-expired") {
        setError("הקוד פג תוקף. שלחו קוד חדש.");
      } else if (
        err.code === "permission-denied" ||
        msg.includes("permission-denied") ||
        msg.includes("PERMISSION_DENIED") ||
        msg.includes("Missing or insufficient permissions")
      ) {
        setError(
          "מספר הטלפון אינו מורשה להרשמה. ההרשמה למערכת היא בהזמנה בלבד — אנא קבעו שיחת הדרכה."
        );
      } else {
        setError("שגיאה באימות. נסו שוב.");
      }
      setVerificationCode("");
      setTimeout(() => otpInputRef.current?.focus(), 50);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleResendCode = async () => {
    setVerificationCode("");

    // Already on the custom-OTP fallback — re-send via ActiveTrail directly
    // instead of retrying (and re-failing) Firebase first.
    if (usingCustomOtp) {
      setIsSendingCode(true);
      try {
        const res = await sendCustomOtp(customOtpPhone, "register");
        if (res?.success) {
          setCountdown(60);
          setTimeout(() => otpInputRef.current?.focus(), 100);
        } else {
          setError("שגיאה בשליחת הקוד. נסו שוב.");
        }
      } catch (e) {
        console.error("[CK-AUTH-008] Custom OTP resend error:", {
          errorCode: "CK-AUTH-008",
          errorMessage: e.message,
          timestamp: new Date().toISOString(),
        });
        setError("שגיאה בשליחת הקוד. נסו שוב.");
      } finally {
        setIsSendingCode(false);
      }
      return;
    }

    setIsCodeSent(false);
    setConfirmationResult(null);
    handleSendCode();
  };

  const handleBack = () => {
    dispatch(prevStep());
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <ShieldCheck size={24} weight="duotone" className="step-icon" />
        <h2>אימות ואישור</h2>
        <p>הזינו מספר טלפון לאימות החשבון</p>
      </div>

      <div className="step-form">
        {!isCodeSent ? (
          <>
            <div className="form-group">
              <label className="required">מספר טלפון</label>
              <PhoneInput
                value={phoneNumber}
                onChange={(val) => {
                  setPhoneNumber(val);
                  setDuplicateError("");
                }}
                placeholder="050-123-4567"
                error={error}
              />
              <p className="field-hint">נשלח קוד אימות חד-פעמי למספר זה</p>
            </div>

            {duplicateError && (
              <div className="duplicate-error-box">
                <WarningCircle size={22} weight="fill" />
                <div className="duplicate-error-content">
                  <p>{duplicateError}</p>
                  <Link to="/login" className="duplicate-error-link">
                    מעבר למסך הכניסה
                  </Link>
                </div>
              </div>
            )}

            {/* Onboarding-call offer — temporarily hidden
            <div className="onboarding-section-wrapper">
              <div className={`onboarding-box ${wantsOnboarding ? "confirmed" : ""}`}>
                {!wantsOnboarding ? (
                  <>
                    <div className="onboarding-box-icon">
                      <Headset size={28} weight="duotone" />
                    </div>
                    <h3 className="onboarding-box-title">רוצים שנעזור לכם להתחיל?</h3>
                    <p className="onboarding-box-subtitle">שיחת הדרכה קצרה של 15 דקות, ללא עלות</p>
                    <ul className="onboarding-box-benefits">
                      <li>נדריך אתכם בהגדרת המערכת ונענה על כל שאלה</li>
                      <li>תתחילו לעבוד מהר יותר ותפיקו את המקסימום</li>
                      <li>בזמן שנוח לכם - גם בשעות הערב</li>
                    </ul>
                    <button
                      type="button"
                      className="onboarding-box-btn"
                      onClick={() => setWantsOnboarding(true)}
                    >
                      {aboutYou.gender === "female"
                        ? "אני מעוניינת שתתאמו איתי שיחה קצרה ללא עלות"
                        : "אני מעוניין שתתאמו איתי שיחה קצרה ללא עלות"}
                    </button>
                  </>
                ) : (
                  <div className="onboarding-box-confirmed">
                    <div className="onboarding-confirmed-icon">
                      <CheckCircle size={40} weight="fill" />
                    </div>
                    <h3 className="onboarding-confirmed-title">מעולה!</h3>
                    <p className="onboarding-confirmed-text">ניצור איתך קשר בהקדם לתיאום השיחה</p>
                    <button
                      type="button"
                      className="onboarding-undo-btn"
                      onClick={() => setWantsOnboarding(false)}
                    >
                      ביטול הבקשה
                    </button>
                  </div>
                )}
              </div>
            </div>
            */}

            <div className="step-actions step-actions-between">
              <CustomClinikitButton
                type="button"
                variant="ghost"
                size="medium"
                onClick={handleBack}
              >
                <ArrowRight size={18} weight="bold" />
                חזרה
              </CustomClinikitButton>
              <CustomClinikitButton
                type="button"
                variant="primary"
                size="large"
                onClick={handleSendCode}
                disabled={isSendingCode || isCheckingDuplicate || !phoneNumber || !!duplicateError}
              >
                {isSendingCode || isCheckingDuplicate ? (
                  <>
                    <LoaderLoop size="xs" />
                    {isCheckingDuplicate ? "בודק..." : "שולח..."}
                  </>
                ) : (
                  <>
                    <Phone size={18} weight="bold" />
                    שלח קוד אימות
                  </>
                )}
              </CustomClinikitButton>
            </div>
          </>
        ) : (
          <>
            <div className="form-group">
              <label className="code-label">
                הזינו את הקוד שנשלח ל-{phoneNumber}
              </label>
              <SegmentedOTPInput
                ref={otpInputRef}
                value={verificationCode}
                onChange={handleCodeChange}
                onComplete={handleCodeComplete}
                length={6}
                disabled={isRegistering}
                error={error}
                autoFocus
              />
            </div>

            <div className="resend-section">
              {countdown > 0 ? (
                <span className="countdown">
                  ניתן לשלוח שוב בעוד {countdown} שניות
                </span>
              ) : (
                <button
                  type="button"
                  className="resend-button"
                  onClick={handleResendCode}
                  disabled={isSendingCode}
                >
                  שלח קוד חדש
                </button>
              )}
            </div>

            <div className="step-actions step-actions-between">
              <CustomClinikitButton
                type="button"
                variant="ghost"
                size="medium"
                onClick={() => {
                  setIsCodeSent(false);
                  setVerificationCode("");
                  setError("");
                  setUsingCustomOtp(false);
                  setCustomOtpPhone("");
                  setConfirmationResult(null);
                }}
              >
                <ArrowRight size={18} weight="bold" />
                שנה מספר
              </CustomClinikitButton>
              <CustomClinikitButton
                type="button"
                variant="primary"
                size="large"
                onClick={() => handleVerifyAndRegister(verificationCode)}
                disabled={isRegistering || verificationCode.length !== 6}
              >
                {isRegistering ? (
                  <>
                    <LoaderLoop size="xs" />
                    מסיים הרשמה...
                  </>
                ) : (
                  <>
                    <Check size={18} weight="bold" />
                    סיים הרשמה
                  </>
                )}
              </CustomClinikitButton>
            </div>
          </>
        )}
      </div>

      <div id="recaptcha-container"></div>

      <p className="recaptcha-attribution">
        אתר זה מוגן על ידי reCAPTCHA, ומדיניות{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          הפרטיות
        </a>{" "}
        ו
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          תנאי השימוש
        </a>{" "}
        של Google חלים.
      </p>
    </div>
  );
}

export default VerificationStep;
